import { Controller } from "@hotwired/stimulus";
import { t, format } from "../lib/i18n";

// Broadcast so sibling multi-selects close themselves, rather than reaching into
// the Stimulus registry to call methods on other controller instances.
const CLOSE_EVENT = "multi-select:opened";

let labelSequence = 0;

export default class extends Controller {

  static targets = ["container", "trigger", "dropdown", "selectedItems", "hiddenInput",
    "hiddenInputsContainer", "clear", "search", "options", "noResults"];

  static values = {
    searchable: Boolean,
    remote: { type: Boolean, default: false },
    url: String,
    preselected: String,
    autosubmit: { type: Boolean, default: false },
    // Quick-filter groups: [{ label, values: [...] }]. Empty means no quick filters.
    quickFilters: Array,
    quickFiltersHeading: String,
    optionsHeading: String,
  }

  connect() {
    this.selected = new Set();
    this.optionCache = new Map();
    this.loading = false;
    this.debounceTimer = null;
    this.lastQuery = null;
    // Namespaces every generated DOM id to this instance, so two multi-selects on the
    // same page cannot collide on a shared option id.
    this.uid = (labelSequence += 1);

    // Bind once: connect() and disconnect() must pass the *same* function reference,
    // or the listener outlives the controller.
    this.boundOutsideClick = this.handleOutsideClick.bind(this);
    this.boundSiblingOpened = this.handleSiblingOpened.bind(this);
    this.boundTriggerKeydown = this.handleTriggerKeydown.bind(this);
    this.boundDropdownKeydown = this.handleDropdownKeydown.bind(this);

    document.addEventListener("click", this.boundOutsideClick);
    document.addEventListener(CLOSE_EVENT, this.boundSiblingOpened);
    this.triggerTarget.addEventListener("keydown", this.boundTriggerKeydown);
    this.dropdownTarget.addEventListener("keydown", this.boundDropdownKeydown);

    this.ensureHiddenInputsContainer();
    this.setupAccessibility();

    if (this.preselectedValue) {
      this.preselectedValue
        .split(",")
        .filter((id) => id.trim() !== "")
        .forEach((id) => this.selected.add(id));
    }
    this.initializeCheckedValues();

    if (this.searchableValue) {
      this.initializeSearch();
    }
    // Built once here rather than on every open, so the DOM is stable.
    this.renderQuickFilters();
    this.updateSelectedItems();

    if (this.remoteValue && this.selected.size > 0) {
      this.fetchOptionsByIds(Array.from(this.selected)).then((options) => {
        this.renderRemoteOptions(options);
        this.updateSelectedItems();
      });
    }
  }

  disconnect() {
    document.removeEventListener("click", this.boundOutsideClick);
    document.removeEventListener(CLOSE_EVENT, this.boundSiblingOpened);
    this.triggerTarget.removeEventListener("keydown", this.boundTriggerKeydown);
    this.dropdownTarget.removeEventListener("keydown", this.boundDropdownKeydown);
    clearTimeout(this.debounceTimer);
  }

  ensureHiddenInputsContainer() {
    if (this.hasHiddenInputsContainerTarget || !this.hasHiddenInputTarget) { return; }

    const container = document.createElement("div");
    container.className = "hidden-inputs-container";
    container.setAttribute("data-multi-select-target", "hiddenInputsContainer");
    this.hiddenInputTarget.insertAdjacentElement("afterend", container);
  }

  initializeCheckedValues() {
    this.element.querySelectorAll('input[type="checkbox"]:checked').forEach((option) => {
      this.selected.add(option.value);
    });
  }

  setupAccessibility() {
    this.triggerTarget.setAttribute("role", "combobox");
    this.triggerTarget.setAttribute("aria-haspopup", "listbox");
    this.triggerTarget.setAttribute("aria-expanded", "false");

    this.dropdownTarget.setAttribute("role", "listbox");
    this.dropdownTarget.setAttribute("aria-multiselectable", "true");

    const labelId = `multi-select-label-${this.uid}`;
    const label = document.createElement("span");
    label.id = labelId;
    label.className = "sr-only";
    label.textContent = this.originalText || t("views.shared.multi_select.select_options", "Select options");
    this.element.insertBefore(label, this.element.firstChild);
    this.triggerTarget.setAttribute("aria-labelledby", labelId);

    this.announcer = document.createElement("div");
    this.announcer.className = "sr-only";
    this.announcer.setAttribute("aria-live", "polite");
    this.element.appendChild(this.announcer);
  }

  get originalText() {
    return this.triggerTarget.dataset.originalText;
  }

  initializeSearch() {
    if (this.hasSearchTarget) { return; }

    const searchDiv = document.createElement("div");
    searchDiv.className = "multi-select-search";
    const input = document.createElement("input");
    input.type = "text";
    input.placeholder = t("general.search", "Search");
    input.dataset.multiSelectTarget = "search";
    input.dataset.action = "input->multi-select#filterOptions";
    searchDiv.appendChild(input);

    const noResults = document.createElement("div");
    noResults.className = "no-results hidden";
    noResults.textContent = t("views.shared.multi_select.no_matches", "No matching options");
    noResults.setAttribute("data-multi-select-target", "noResults");

    this.dropdownTarget.insertBefore(searchDiv, this.dropdownTarget.firstChild);
    this.dropdownTarget.appendChild(noResults);
  }

  handleOutsideClick(event) {
    if (!event || !this.element.contains(event.target)) {
      this.closeDropdown();
    }
  }

  handleSiblingOpened(event) {
    if (event.detail?.source !== this.element) {
      this.closeDropdown();
    }
  }

  filterOptions(event) {
    const searchText = event.target.value.toLowerCase();

    if (this.remoteValue) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => this.fetchRemoteOptions(searchText), 300);
      return;
    }

    let hasVisibleOptions = false;
    this.dropdownTarget.querySelectorAll(".multi-select-option-filterable").forEach((option) => {
      const isMatch = option.querySelector("label").textContent.toLowerCase().includes(searchText);
      option.style.display = isMatch ? "flex" : "none";
      if (isMatch) { hasVisibleOptions = true; }
    });

    if (this.hasNoResultsTarget) {
      this.noResultsTarget.classList.toggle("hidden", hasVisibleOptions);
    }
  }

  async fetchRemoteOptions(query) {
    if (query === this.lastQuery && this.lastQuery !== null) { return; }

    this.lastQuery = query;
    this.showLoadingState();

    try {
      const response = await fetch(`${this.urlValue}?q=${encodeURIComponent(query)}`);
      if (!response.ok) { throw new Error(`API error: ${response.status}`); }

      const data = await response.json();
      const users = data.users || [];
      users.forEach((user) => this.optionCache.set(user.id.toString(), user));

      // With no query the list doubles as the current selection, so fill in any
      // selected option the search did not return.
      if (query.length === 0) {
        const known = new Set(users.map((user) => user.id.toString()));
        const missing = Array.from(this.selected).filter((id) => !known.has(id));
        users.push(...await this.fetchOptionsByIds(missing));
      }

      this.renderRemoteOptions(users);
    } catch (error) {
      console.error("Error fetching remote options:", error);
      if (this.hasOptionsTarget) {
        this.optionsTarget.replaceChildren(
          this.buildMessage("error-message", t("views.shared.multi_select.error_loading", "Error loading options")),
        );
      }
    } finally {
      this.loading = false;
    }
  }

  // Resolves ids the search endpoint did not return (or that were preselected),
  // in parallel and via the cache.
  async fetchOptionsByIds(ids) {
    const results = await Promise.all(ids.map(async (id) => {
      const key = id.toString();
      if (this.optionCache.has(key)) { return this.optionCache.get(key); }

      try {
        const response = await fetch(`${this.urlValue}/${encodeURIComponent(id)}`);
        if (!response.ok) { return null; }

        const option = await response.json();
        if (!option?.id) { return null; }

        this.optionCache.set(option.id.toString(), option);
        return option;
      } catch (error) {
        console.error(`Error fetching option with ID ${id}:`, error);
        return null;
      }
    }));

    return results.filter(Boolean);
  }

  buildMessage(className, text) {
    const node = document.createElement("div");
    node.className = className;
    node.textContent = text;
    if (className === "loading") {
      node.setAttribute("role", "status");
      node.setAttribute("aria-live", "polite");
    }
    return node;
  }

  showLoadingState() {
    this.loading = true;
    if (!this.hasOptionsTarget) { return; }

    this.optionsTarget.replaceChildren(
      this.buildMessage("loading", t("views.shared.multi_select.loading", "Loading")),
    );
  }

  renderRemoteOptions(users) {
    if (!this.hasOptionsTarget) { return; }

    if (users.length === 0) {
      this.optionsTarget.replaceChildren(
        this.buildMessage("no-results", t("views.shared.multi_select.no_results", "No results found")),
      );
      return;
    }

    const unique = new Map(users.map((user) => [user.id.toString(), user]));
    // Selected options float to the top.
    const sorted = Array.from(unique.values()).sort((a, b) => {
      const aSelected = this.selected.has(a.id.toString());
      const bSelected = this.selected.has(b.id.toString());
      return Number(bSelected) - Number(aSelected);
    });

    this.optionsTarget.replaceChildren(...sorted.map((user) => this.buildRemoteOption(user)));
  }

  buildRemoteOption(user) {
    const userId = user.id.toString();
    const wrapper = document.createElement("div");
    wrapper.className = "multi-select-option multi-select-option-filterable";

    const input = document.createElement("input");
    input.type = "checkbox";
    input.id = `multi-select-${this.uid}-option-${userId}`;
    input.value = userId;
    input.dataset.action = "change->multi-select#toggleOption";
    input.dataset.mainText = user.full_name || "";
    input.checked = this.selected.has(userId);

    const label = document.createElement("label");
    label.htmlFor = input.id;

    const content = document.createElement("div");
    content.className = "multi-select-option-content";

    const mainText = document.createElement("span");
    mainText.className = "option-main-text";
    mainText.textContent = user.full_name || "";

    const subText = document.createElement("span");
    subText.className = "option-subtext";
    subText.textContent = user.org_name || "";

    content.append(mainText, subText);
    label.appendChild(content);
    wrapper.append(input, label);

    return wrapper;
  }

  toggleDropdown(event) {
    const opening = !this.dropdownTarget.classList.contains("is-open");

    if (opening) {
      document.dispatchEvent(new CustomEvent(CLOSE_EVENT, { detail: { source: this.element } }));
    }

    this.triggerTarget.classList.toggle("is-open", opening);
    this.dropdownTarget.classList.toggle("is-open", opening);
    this.triggerTarget.setAttribute("aria-expanded", opening.toString());

    if (opening && this.remoteValue) {
      this.fetchRemoteOptions("");
    }

    if (opening && this.searchableValue && this.hasSearchTarget) {
      this.searchTarget.value = "";
      this.searchTarget.focus();
      this.filterOptions({ target: this.searchTarget });
    }

    event.stopPropagation();
  }

  announce(message) {
    if (!this.announcer) { return; }

    this.announcer.textContent = message;
    setTimeout(() => { this.announcer.textContent = ""; }, 1000);
  }

  toggleOption(event) {
    const option = event.target;
    const value = option.value;
    const optionText = option.dataset.mainText || option.closest("label")?.textContent?.trim() || "";

    if (this.selected.has(value)) {
      this.selected.delete(value);
      option.classList.remove("selected");
      this.announce(format("views.shared.multi_select.removed", "Removed %{option}", { option: optionText }));
    } else {
      this.selected.add(value);
      option.classList.add("selected");
      this.announce(format("views.shared.multi_select.added", "Added %{option}", { option: optionText }));
    }

    this.updateSelectedItems();
    this.autoSubmitForm();
  }

  updateSelectedItems() {
    if (this.hasSelectedItemsTarget) {
      this.selectedItemsTarget.replaceChildren();
    }
    this.updateHiddenInputs();
    this.updateChipLabel();
    this.updateQuickFilterCheckboxStates();
  }

  updateHiddenInputs() {
    if (!this.hasHiddenInputTarget) { return; }

    if (!this.hasHiddenInputsContainerTarget) {
      this.hiddenInputTarget.value = Array.from(this.selected).join(",");
      return;
    }

    const inputName = this.hiddenInputTarget.name;
    this.hiddenInputsContainerTarget.replaceChildren(...Array.from(this.selected).map((value) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = inputName;
      input.value = value;
      return input;
    }));
  }

  closeDropdown() {
    this.dropdownTarget.classList.remove("is-open");
    this.triggerTarget.classList.remove("is-open");
    this.triggerTarget.setAttribute("aria-expanded", "false");

    if (this.searchableValue && this.hasSearchTarget) {
      this.searchTarget.value = "";
      this.dropdownTarget.querySelectorAll(".multi-select-option").forEach((option) => {
        option.style.display = "flex";
      });
      if (this.hasNoResultsTarget) {
        this.noResultsTarget.classList.add("hidden");
      }
    }
    this.updateChipLabel();
  }

  updateChipLabel() {
    const count = this.selected.size;
    const label = this.originalText;

    if (count > 0) {
      this.triggerTarget.textContent = `(${count}) ${label}`;
      this.triggerTarget.setAttribute(
        "aria-label",
        format("views.shared.multi_select.selected_count", "%{count} items selected. %{label}", { count, label }),
      );
      this.clearTarget.classList.remove("hidden");
      this.clearTarget.setAttribute("aria-label", t("views.shared.multi_select.clear_all", "Clear all selected items"));
      this.containerTarget.classList.add("is-active");
    } else {
      this.triggerTarget.textContent = label;
      this.triggerTarget.setAttribute("aria-label", label);
      this.clearTarget.classList.add("hidden");
      this.containerTarget.classList.remove("is-active");
    }
  }

  clear(event) {
    event.stopPropagation();

    const count = this.selected.size;
    this.deselectAll();
    this.closeDropdown();

    if (count > 0) {
      this.announce(format("views.shared.multi_select.cleared", "Cleared %{count} selected items", { count }));
      this.autoSubmitForm();
    }

    setTimeout(() => this.triggerTarget.focus(), 0);
  }

  deselectAll() {
    this.element.querySelectorAll('input[type="checkbox"]').forEach((checkbox) => {
      checkbox.checked = false;
      checkbox.indeterminate = false;
    });
    this.element.querySelectorAll(".multi-select-option.selected")
      .forEach((option) => option.classList.remove("selected"));
    this.selected.clear();
    this.updateSelectedItems();
  }

  handleTriggerKeydown(event) {
    switch (event.key) {
      case "Enter":
      case " ":
      case "ArrowDown":
        event.preventDefault();
        this.toggleDropdown(event);
        break;
      case "Escape":
        event.preventDefault();
        this.closeDropdown();
        break;
    }
  }

  handleDropdownKeydown(event) {
    switch (event.key) {
      case "Escape":
        event.preventDefault();
        this.closeDropdown();
        this.triggerTarget.focus();
        break;
      case "ArrowDown":
        event.preventDefault();
        this.focusNextOption(1);
        break;
      case "ArrowUp":
        event.preventDefault();
        this.focusNextOption(-1);
        break;
      case " ":
      case "Enter":
        if (event.target.type === "checkbox") {
          event.preventDefault();
          event.target.click();
        }
        break;
      case "Tab":
        if (event.shiftKey && document.activeElement === this.dropdownTarget.querySelector('input[type="checkbox"]')) {
          this.closeDropdown();
        }
        break;
    }
  }

  focusNextOption(direction) {
    const options = Array.from(this.dropdownTarget.querySelectorAll('input[type="checkbox"]:not([disabled])'));
    if (options.length === 0) { return; }

    const currentIndex = options.indexOf(document.activeElement);
    if (currentIndex === -1) {
      options[direction > 0 ? 0 : options.length - 1].focus();
      return;
    }

    options[(currentIndex + direction + options.length) % options.length].focus();
  }

  get quickFilterGroups() {
    return Array.isArray(this.quickFiltersValue) ? this.quickFiltersValue : [];
  }

  renderQuickFilters() {
    const groups = this.quickFilterGroups;
    if (groups.length === 0) { return; }

    const container = document.createElement("div");
    container.className = "quick-filters";
    container.setAttribute("role", "group");
    if (this.quickFiltersHeadingValue) {
      container.setAttribute("aria-label", this.quickFiltersHeadingValue);
      container.appendChild(this.buildGroupHeading(this.quickFiltersHeadingValue));
    }

    groups.forEach((group, index) => {
      const checkbox = document.createElement("input");
      checkbox.type = "checkbox";
      checkbox.className = "quick-filter-checkbox";
      checkbox.id = `multi-select-${this.uid}-quick-filter-${index}`;
      checkbox.dataset.values = JSON.stringify(group.values || []);
      checkbox.dataset.action = "change->multi-select#applyQuickFilterCheckbox";

      const label = document.createElement("label");
      label.htmlFor = checkbox.id;
      label.className = "quick-filter-label";
      label.textContent = group.label;

      const wrapper = document.createElement("div");
      wrapper.className = "multi-select-option";
      wrapper.append(checkbox, label);
      container.appendChild(wrapper);
    });

    const separator = document.createElement("div");
    separator.className = "quick-filter-separator";
    container.appendChild(separator);

    if (this.optionsHeadingValue) {
      container.appendChild(this.buildGroupHeading(this.optionsHeadingValue));
    }

    const searchElement = this.dropdownTarget.querySelector(".multi-select-search");
    if (searchElement) {
      searchElement.after(container);
    } else {
      this.dropdownTarget.prepend(container);
    }
  }

  buildGroupHeading(text) {
    const heading = document.createElement("div");
    heading.className = "multi-select-option-group";
    const span = document.createElement("span");
    span.textContent = text;
    heading.appendChild(span);
    return heading;
  }

  applyQuickFilterCheckbox(event) {
    const checkbox = event.currentTarget;
    const values = JSON.parse(checkbox.dataset.values || "[]");
    const checked = checkbox.checked;
    let changed = false;

    this.optionCheckboxes().forEach((option) => {
      if (!values.includes(option.value)) { return; }

      if (option.checked !== checked) {
        option.checked = checked;
        changed = true;
      }
      if (checked) {
        this.selected.add(option.value);
      } else {
        this.selected.delete(option.value);
      }
    });

    if (changed) {
      this.updateSelectedItems();
      this.autoSubmitForm();
    } else {
      this.updateQuickFilterCheckboxStates();
    }
  }

  optionCheckboxes() {
    return this.dropdownTarget.querySelectorAll('input[type="checkbox"]:not(.quick-filter-checkbox)');
  }

  updateQuickFilterCheckboxStates() {
    const quickFilterCheckboxes = this.dropdownTarget.querySelectorAll(".quick-filter-checkbox");
    if (quickFilterCheckboxes.length === 0) { return; }

    const checkedValues = new Set(
      Array.from(this.optionCheckboxes()).filter((cb) => cb.checked).map((cb) => cb.value),
    );

    quickFilterCheckboxes.forEach((quickFilter) => {
      const values = JSON.parse(quickFilter.dataset.values || "[]");
      const checkedCount = values.filter((value) => checkedValues.has(value)).length;

      quickFilter.checked = checkedCount > 0 && checkedCount === values.length;
      quickFilter.indeterminate = checkedCount > 0 && checkedCount < values.length;
    });
  }

  autoSubmitForm() {
    if (!this.autosubmitValue) { return; }

    const form = this.element.closest("form");
    if (!form) { return; }

    if (typeof Rails !== "undefined" && form.dataset.remote === "true") {
      Rails.fire(form, "submit");
    } else {
      form.requestSubmit();
    }

    this.announce(t("views.shared.multi_select.submitted", "Filters updated"));
  }
}
